import { emailSchema } from '../../schemas/common/email.js';
import '../../schemas/common/url.js';
import { phoneSchema } from '../../schemas/common/phone.js';
import '../../schemas/common/dateOfBirth.js';
import { nameSchema } from '../../schemas/common/name.js';
import '../../schemas/common/license.js';
import '../../schemas/common/financial.js';
import '../../schemas/common/checkbox.js';
import '../../schemas/common/hours.js';
import '../../schemas/common/tcpa.js';
import { z } from 'zod';
import { repositoryObjectSchema } from '../Repository.js';
import { supportScheduleSchema } from '../SupportHours.js';
import { onboardingCodeSchema } from './constants/OnboardingCode.js';
import { serviceAccessObjectSchema } from './serviceAccess/serviceAccess.js';
import { servicesProviderConfigSchema } from './ServicesProviderConfig.js';

//--------------------------------
// Primary Contact Schema
//--------------------------------
const primaryContactSchema = z.object({
    fullName: nameSchema,
    phone: phoneSchema,
    title: z.string(),
    email: emailSchema,
});
//--------------------------------
// Styling Map Schema
//--------------------------------
const stylingMapSchema = z.object({
    brandColor: z.string().optional(),
    brandColorLight: z.string().optional(),
});
//--------------------------------
// Plan Details Schema
//--------------------------------
const reloadScheduleSchema = z.object({
    amount: z.number(),
    label: z.string(), // July 1st
    date: z.number(), // 1238123717230
    waived: z.boolean().optional(), // if true, this reload schedule is waived
});
const expiryScheduleSchema = z.object({
    label: z.string(), // July 1st
    date: z.number(), // 1238123717230
    waived: z.boolean().optional(), // if true, this expiry schedule is waived
});
const planDetailsSchema = z.object({
    reloadAmount: z.union([z.number(), z.string()]).optional(), // e.g., 25000
    // credit amount purchased by Client per credit billing cycle (reloadFrequency)
    reloadSchedules: z.array(reloadScheduleSchema).optional(),
    reloadFrequency: z.enum(['monthly', 'quarterly', 'biannually', 'annually', 'custom']),
    // frequency at which Client is contractually obligated to
    // purchase the reloadAmount
    // occurs at start of term, e.g., Jan 1st 12:00 AM
    expirySchedules: z.array(expiryScheduleSchema).optional(),
    expiryFrequency: z.enum(['monthly', 'quarterly', 'biannually', 'annually', 'custom']),
    // frequency at which all credits a Client possesses expire
    // occurs at end of term, e.g., Dec 31st 11:59 PM
    contractType: z.enum(['PREPAID', 'MINIMUMS']),
    contractStart: z.number(), // when contract becomes active (epoch)
    contractEnd: z.number(), // when contract becomes inactive (epoch)
});
//--------------------------------
// Agent Capacity Enum
//--------------------------------
const AgentCapacity = {
    AGENT: 'AGENT',
    OWNER: 'OWNER',
    ADMIN: 'ADMIN',
};
const agentCapacityValues = [
    AgentCapacity.AGENT,
    AgentCapacity.OWNER,
    AgentCapacity.ADMIN,
];
const agentCapacitySchema = z.enum(agentCapacityValues);
//--------------------------------
// Agent Schema
//--------------------------------
const agentSchema = z.object({
    firstName: nameSchema,
    middleName: nameSchema.nullable(),
    lastName: nameSchema,
    email: emailSchema,
    capacity: agentCapacitySchema,
});
//--------------------------------
// Support Object Schema
//--------------------------------
const supportObjectSchema = z.object({
    email: emailSchema,
    phone: phoneSchema,
    ekhoPhone: phoneSchema,
    schedule: supportScheduleSchema,
});
//--------------------------------
// Generic Services Provider Schema
//--------------------------------
const servicesProviderGenericDataSchema = repositoryObjectSchema.extend({
    programOwnerIds: z.array(z.string()), // the program owner ids that this services provider is associated with
    uniqueReadableKey: z.string(), // used for Hubspot, slack, etc.
    name: nameSchema, // display name
    email: emailSchema, // email for notifications to this SVP
    phone: phoneSchema, // phone number for notifications to this SVP
    onboardingCompleted: z.boolean(), // true once onboarding has been finalized
    serviceAccess: serviceAccessObjectSchema, // used for onboarding flow service configuration options
    onboardingCode: onboardingCodeSchema.optional(), // onboarding flow code for backwards compatibility
    onboardingPage: z.number().optional(), // page number for the onboarding flow
    invitedEmails: z.array(z.string()).optional(), // emails that have been invited to the onboarding flow
    primaryContact: primaryContactSchema,
    config: servicesProviderConfigSchema, // TODO: Convert ServicesProviderConfig to Zod
    latestOrderNumber: z.number().optional(), // counter for sequential order numbers assigned to this provider
    // Dealer context fields (populated by scraping)
    hoursDescription: z.string().optional(),
    address: z.string().optional(),
    timezone: z.string().optional(),
    websiteUrl: z.string().optional(),
    businessHours: z
        .object({
        sales: z.record(z.string(), z.object({ open: z.string(), close: z.string() }).nullable()),
        service: z.record(z.string(), z.object({ open: z.string(), close: z.string() }).nullable()),
    })
        .optional(),
    brands: z.array(z.string()).optional(),
    aboutText: z.string().optional(),
    lastContextScraped: z.number().optional(),
    lastContextScrapeJobId: z.string().optional(),
});

export { AgentCapacity, agentCapacitySchema, agentCapacityValues, agentSchema, planDetailsSchema, primaryContactSchema, servicesProviderGenericDataSchema, stylingMapSchema, supportObjectSchema };
//# sourceMappingURL=ServicesProviderData.js.map
